import classnames from 'classnames';
import React, { useState, useRef, useEffect } from 'react';
import { useForm } from '../Form';
import { Icon } from '../../Icon/Icon';
import './../input.scss';
import { t } from '../../../locales/translate';
import { Spinner } from '../../Spinner/Spinner';
import { TextFieldBase } from './TextField';
import { round } from '../../../util/util';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

export interface Props extends TextFieldBase {
  ignoreValidationError?: boolean;
  value?: number;
  onChange?: (value: number) => void;
  onFocus?: (value: number) => void;
  onBlur?: (value: number) => void;
}

export function PercentageField({
  name,
  label,
  icon,
  actions,
  description,
  placeholder,
  errorMessage,
  autoFocus,
  required,
  disabled,
  readOnly,
  className,
  min,
  max,
  step = 'any',
  value,
  ignoreValidationError,
  displayOptional = true,
  loading,
  onChange,
  onFocus,
  onBlur,
}: Props): JSX.Element {
  const [contextValue, setValue, triedToSubmit, notifyValidity] = useForm<number>(name || '');

  const [active, setActive] = useState(false);
  const [touched, setTouched] = useState(false);

  const parsedValue = round(parseFloat(`${value || contextValue}`) * 100, 2);

  const formattedValue = !isNaN(parsedValue) ? parsedValue : undefined;

  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);

  useEffect(() => {
    return () => {
      notifyValidity(true);
    };
  }, []);

  const validationMessage =
    (ignoreValidationError && errorMessage) || ((triedToSubmit || touched) && inputRef.current?.validationMessage);

  const valid = (required ? !!formattedValue : true) && !validationMessage;

  notifyValidity(valid);

  function isFull(): boolean {
    if (active) {
      return true;
    }
    if (placeholder) {
      return true;
    }
    if (formattedValue === 0) {
      return true;
    }
    if (formattedValue) {
      return true;
    }
    return false;
  }

  return (
    <div className={classnames('input', { 'input--with-icon': icon }, { 'input--disabled': disabled }, className)}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': isFull(),
          'input__container--error': validationMessage,
        })}
      >
        <div className="input__input-wrapper">
          {label && (
            <label className="input__label" htmlFor={name}>
              {label}
              {displayOptional && !required && ` (${t.general.optional})`}
            </label>
          )}
          <input
            id={name}
            name={name}
            className="input__input"
            value={formattedValue !== undefined ? formattedValue : ''}
            type="text"
            onChange={(v) => {
              const value = parseFloat(v.target.value) / 100;
              if (readOnly) {
                v.preventDefault();
                return;
              }
              if (onChange) {
                onChange(value);
              } else {
                setValue(value);
              }
              if (value !== 0) {
                setTimeout(() => setTouched(true), 100);
              }
            }}
            onFocus={() => {
              setActive(true);
              if (onFocus) {
                onFocus(value || 0);
              }
            }}
            onBlur={() => {
              setActive(false);
              if (onBlur) {
                onBlur(value || 0);
              }
            }}
            autoFocus={autoFocus}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            ref={(ref) => {
              inputRef.current = ref;
            }}
            min={min}
            max={max}
            step={step}
          />
          {actions && <div className="input__actions">{actions}</div>}
          {validationMessage && <Icon name="alert-circle--sm" className="input__alert-circle" />}
          {loading && <Spinner className="input__loading" color="var(--color-text-label)" scale="0.6" />}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          <Collapse open={validationMessage ? true : false}>
            <div className="input__error-message">{validationMessage}</div>
          </Collapse>
        </div>
      </div>
    </div>
  );
}
